import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { selectRoles } from "../../features/roleSlice";
import { featuredTools } from "./featuredData";

const canSee = (needed, mine) =>
  !needed || needed[0] === "*" || needed.some((role) => mine.includes(role));

// Slim one-row spotlight that rotates daily through featuredData.js —
// replaces the old full-width NeedAQuote marketing card.
const FeaturedTool = () => {
  const role = useSelector(selectRoles);
  const userRoles = role?.roles || [];
  const tools = featuredTools.filter((tool) => canSee(tool.roles, userRoles));

  if (tools.length === 0) return null;

  // Deterministic daily rotation, no per-render randomness.
  const tool = tools[Math.floor(Date.now() / 86400000) % tools.length];

  const cta = (
    <>
      {tool.cta}
      <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
    </>
  );
  const ctaClass = "app-btn app-btn-primary relative shrink-0 self-start sm:self-auto";

  return (
    // Same soft violet→sky wash as the hero so the banner reads as a
    // highlight without a heavy dark block; the glow layer is clipped in its
    // own box so the card itself never needs overflow tricks.
    <section
      aria-label="Featured tool"
      className="app-card relative isolate flex flex-col gap-4 rounded-2xl border p-5 sm:flex-row sm:items-center"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-[inherit]"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/[0.12] via-sky-500/[0.08] to-transparent dark:from-violet-500/20 dark:via-sky-500/10" />
        <div className="absolute -left-10 -top-16 h-40 w-40 rounded-full bg-violet-400/25 blur-3xl dark:bg-violet-500/25" />
      </div>
      <span className="app-icon-tile h-12 w-12 rounded-2xl bg-gradient-to-br from-violet-500 to-sky-500 text-white shadow-[0_6px_16px_-6px_rgba(124,58,237,0.6)]">
        <AutoAwesomeRoundedIcon sx={{ fontSize: 24 }} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="app-eyebrow text-violet-600 dark:text-violet-300">
          Featured tool
        </p>
        <p className="mt-0.5 font-semibold tracking-tight">{tool.name}</p>
        <p className="app-muted mt-0.5 text-sm leading-relaxed sm:truncate">
          {tool.tagline}
        </p>
      </div>
      {tool.href ? (
        <a href={tool.href} target="_blank" rel="noreferrer" className={ctaClass}>
          {cta}
        </a>
      ) : (
        <Link to={tool.to} className={ctaClass}>
          {cta}
        </Link>
      )}
    </section>
  );
};

export default FeaturedTool;
